import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState, Fragment, useContext, useRef } from "react";
import { Transition, Dialog } from "@headlessui/react";
import Button from "../../components/Button";
import { getCart } from "../../utils/localstorage";
import Items from "./Items";
import BillingInfo from "./BillingInfo";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "react-toastify";
import ArrowLeftIcon from "@heroicons/react/24/outline/ArrowLeftIcon";
import ArrowRightIcon from "@heroicons/react/24/outline/ArrowRightIcon";
import { CartContext } from "../../contexts/CartContext";
import { getDiscountPercent, getFixedDiscount } from "../../utils/helper";
import Turnstile from "../../components/Turnstile";

export default function Confirmation() {
  const notify = () => toast.error("Order is Failed!");
  const [searchParams] = useSearchParams();
  const cartContext = useContext(CartContext);
  let navigate = useNavigate();
  const [taxData, setTaxData] = useState();
  const [taxes, setTaxes] = useState(0);
  const [taxesLoading, setTaxesLoading] = useState(true);
  const [shippingRate, setShippingRate] = useState(0);
  const [shippingRateLoading, setShippingRateLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpenOrderFailedModal, setIsOpenOrderFailedModal] = useState(false);
  // Turnstile: the latest token lives in a ref so the PayPal onApprove callback always sends a current one.
  const turnstileTokenRef = useRef("");
  const [isTurnstileReady, setIsTurnstileReady] = useState(false);
  // If Turnstile cannot load, card checkout still proceeds and the Orders API decides (Monitor allows, Enforce rejects
  // before any charge). PayPal stays blocked because PayPal captures payment before the order is sent.
  const [isTurnstileUnavailable, setIsTurnstileUnavailable] = useState(false);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const handleTurnstileToken = (token) => {
    turnstileTokenRef.current = token;
    if (token) setIsTurnstileReady(true);
  };
  const shippingInfo = JSON.parse(localStorage.getItem("shippingAddress"));
  const billingInfo = JSON.parse(localStorage.getItem("billingAddress"));
  const cardInfo = JSON.parse(localStorage.getItem("cardInfo"));
  const paymentMethod = localStorage.getItem("paymentMethod");
  const memberWebsitePassword = localStorage.getItem("memberWebsitePassword");
  const memberWebsitePrefix = localStorage.getItem("memberWebsitePrefix");
  const referral = localStorage.getItem("referral");
  const discountCode = localStorage.getItem("discount");
  // const discountPercent = getDiscountPercent(discountCode); // e.g., 10
  // const discountFactor = 1 - discountPercent / 100;

  const fixedDiscount = getFixedDiscount(discountCode);

  const [sponsorId, setSponsorId] = useState(
    searchParams.get("sponsorId") || localStorage.getItem("sponsorId") || 22
  );
  // const [isDiscountApplied, setIsDiscountApplied] = useState(false);
  // const [discountCode, setDiscountCode] = useState("");
  // const [isValid, setIsValid] = useState(null);
  // const [error, setError] = useState("");
  // const [isSubmittingDiscount, setIsSubmittingDiscount] = useState(false);

  useEffect(() => {
    setSponsorId((prev) => {
      if (prev === searchParams.get("sponsorId")) {
        return prev || localStorage.getItem("sponsorId") || 22;
      }
      return (
        searchParams.get("sponsorId") || localStorage.getItem("sponsorId") || 22
      );
    });
  }, [searchParams]);

  const cartProducts = getCart()?.filter(({ product }) => product?.Sku);

  const excludedSkus = [
    "B3SubTD",
    "B3Sub6m",
    "B3Sub12m",
    "BBCC",
    "BBCCadd",
    "BBCCre",
    "BBCCAdv1",
    "BBCCAdv2",
    "BBCCAdv3",
    "BoostTD",
    "B3Bro5",
    "B3Book5Fr",
    "B3UmvpTD"
  ];

  const areAllItemsExcluded = (cartProducts) => {
    return cartProducts.every((item) =>
      excludedSkus.includes(item.product.Sku)
    );
  };

  const [subTotal, setSubTotal] = useState(0);

  useEffect(() => {
    const calculatedSubTotal = cartProducts.reduce((total, p) => {
      return total + p.product.Price * p.qty;
    }, 0);
    setSubTotal(calculatedSubTotal);
  }, [cartProducts]);

  const payload = {
    ...shippingInfo,
    SubTotal: (subTotal - fixedDiscount).toFixed(2),
    LineItems: cartProducts.map((p) => {
      const isDiscountedItem = p.product.Sku === "RCBandSt1";
      const price = isDiscountedItem
        ? Math.max(0, p.product.Price - fixedDiscount)
        : p.product.Price;

      return {
        ItemId: p.product.Sku,
        QtyOrdered: p.qty,
        WholeSalePrice: Number(price.toFixed(2)),
      };
    }),
  };

  useEffect(() => {
    if (subTotal > 0) {
      // Note: Tax is not applied to this cart
      fetch(`${process.env.REACT_APP_API_URI}/api/salestax/avalara`, {
        method: "post",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
        .then((res) => res.json())
        .then((res) => {
          if (res && !res.errors) {
            setTaxes(res.totalTaxAmount);
            setTaxData(res);
            setTaxesLoading(false);
          }
        })
        .catch((err) => {
          toast.error(
            "There was an error calculating the taxes. Please try again later."
          );
        });
    fetch(
      `${process.env.REACT_APP_API_URI}/api/Product/shippingratesforlineitems`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          memberId: sponsorId,
          shipToCountry: shippingInfo.ShipToCountry,
          lineItems: cartProducts.map((p, index) => ({
            itemId: p.product.Sku,
            qtyOrdered: p.qty,
          })),
        }),
      }
    )
      .then((res) => res.json())
      .then((res) => {
        if (res && !res.errors) {
          if (areAllItemsExcluded(cartProducts)) {
              if (referral == "true") {
                setShippingRate(4.99);
              } else {
                setShippingRate(0);
              }
          } else {
            setShippingRate(res);
          }
          setShippingRateLoading(false);
        }
      })
      .catch((err) => {
        toast.error(
          "There was an error calculating the shipping rates. Please try again later."
        );
      });
    }
  }, [subTotal]);

  useEffect(() => {
    const discountedSubTotal = subTotal - fixedDiscount;
    setTotal(discountedSubTotal + Number(shippingRate) || 0);
  }, [subTotal, shippingRate, fixedDiscount]);

  const submit = () => {
    setIsSubmitting(true);
    const payload = {
      affiliate: referral === "true" ? "YES" : "",
      repId: 0,
      shipToFirstName: shippingInfo.ShipToFirstName,
      shipToLastName: shippingInfo.ShipToLastName,
      shipToAddress: shippingInfo.ShipToAddress1,
      shipToAddress2: shippingInfo.ShipToAddress2,
      shipToCity: shippingInfo.ShipToCity,
      shipToState: shippingInfo.ShipToState,
      shipToCountry: shippingInfo.ShipToCountry,
      shipToPostalCode: shippingInfo.ShipToPostalCode,
      shipToPhone: shippingInfo.ShipToPhone,
      shipToEmail: shippingInfo.ShipToEmail,
      country: billingInfo.country,
      billToFirstName: billingInfo.firstName,
      billToLastName: billingInfo.lastName,
      billToAddress: billingInfo.address1,
      billToAddress2: billingInfo.address2,
      billToCity: billingInfo.city,
      billToState: billingInfo.state,
      billToPostalCode: billingInfo.postalCode,
      paymentMethodId: paymentMethod === "credit" ? 1 : 3,
      sponsorId: Number(sponsorId),
      memberWebSiteName: memberWebsitePrefix || "",
      cart: "B3Intl",
      lineItems: cartProducts.map((p, index) => ({
        id: index,
        itemId: p.product.Sku,
        itemDesc: p.product.Description,
        qtyOrdered: Number(p.qty),
        wholeSalePrice: p.product.Price,
        bonus: 0,
        promotion: 0,
      })),
      discountCode: discountCode === "null" ? null : discountCode,
      discount: fixedDiscount,
      payment: {
        cardNumber: paymentMethod === "credit" ? cardInfo?.cardNumber : "",
        expirationDate:
          paymentMethod === "credit"
            ? (cardInfo.expMonth.length === 1
                ? `0${cardInfo.expMonth}`
                : cardInfo.expMonth) + cardInfo.expYear.slice(-2)
            : "",
        securityCode: paymentMethod === "credit" ? cardInfo.cvv : "",
        amount: total,
        name: paymentMethod === "credit" ? cardInfo.cardholder : "",
        firstName: billingInfo.firstName,
        lastName: billingInfo.lastName,
        street: billingInfo.address1,
        city: billingInfo.city,
        state: billingInfo.state,
        zipCode: billingInfo.postalCode,
        phone: billingInfo.phone,
        invoiceNum: "",
        repId: "",
        email: billingInfo.email,
        customerProfileDescription: "",
        customerProfileId: "",
        customerPaymentProfileId: "",
        creditCardLast4Digits:
          paymentMethod === "credit" ? cardInfo?.cardNumber?.slice(-4) : "",
        ipAddress: "",
      },
      turnstileToken: turnstileTokenRef.current,
    };
    const lastPayload = memberWebsitePassword
      ? {
          ...payload,
          passWord: memberWebsitePassword,
        }
      : payload;
    fetch(`${process.env.REACT_APP_API_URI}/api/Order/add`, {
      method: "post",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(lastPayload),
    })
      .then((res) => res.json())
      .then(async (res) => {
        if (res) {
          localStorage.removeItem("cart");
          localStorage.removeItem("billingAddress");
          localStorage.removeItem("shippingAddress");
          localStorage.removeItem("memberWebsitePassword");
          localStorage.removeItem("memberWebsitePrefix")
          localStorage.setItem("orderId", res);
          if (cardInfo && cardInfo.cvv) {
            delete cardInfo.cvv;
            localStorage.setItem("cardInfo", JSON.stringify(cardInfo));
          }
          await fetch(`${process.env.REACT_APP_API_URI}/api/Order/${res}`, {
            method: "get",
          })
            .then((res) => res.json())
            .then((res) => {
              localStorage.setItem("memberId", res.repId);
            })
            .catch((err) => {
              console.log(err);
            });
          localStorage.removeItem("referral");
          navigate("/order-success");
          cartContext.save([]);
          cartContext.setShowError(false);
        } else {
          notify();
          setIsOpenOrderFailedModal(true);
          // Tokens are single-use; get a fresh one before the customer retries.
          setIsTurnstileReady(false);
          setTurnstileResetKey((k) => k + 1);
        }
        setIsSubmitting(false);
      });
  };

  // const validateDiscountCode = async () => {
  //   if (isDiscountApplied) {
  //     toast.info("Discount has already been applied.");
  //     return;
  //   }
  //   if (!discountCode.trim()) {
  //     setIsValid(false);
  //     setError("Enter discount code.");
  //     return;
  //   }
  //   setIsSubmittingDiscount(true);
  //   setIsValid(false);
  //   try {
  //     const response = await fetch(
  //       `${process.env.REACT_APP_API_URI}/api/Order/discountcodevalidation/${discountCode}`
  //     );
  //     const result = await response.json();
  //     if (result === true) {
  //       setIsValid(true);
  //       setError("");
  //       const discountAmount = subTotal * 0.1;
  //       const discountedSubTotal = subTotal - discountAmount;
  //       setSubTotal(discountedSubTotal);
  //       setIsDiscountApplied(true);

  //       const newTotal =
  //         discountedSubTotal + Number(taxes) + Number(shippingRate) || 0;
  //       setTotal(newTotal);
  //     } else {
  //       setIsValid(false);
  //       setError("Invalid discount code.");
  //     }
  //   } catch (err) {
  //     setIsValid(false);
  //     setError("An error occurred while validating the code.");
  //   } finally {
  //     setIsSubmittingDiscount(false);
  //   }
  // };

  const closeModal = () => {
    setIsOpenOrderFailedModal(false);
  };
  const getCardImagePath = (number) => {
    if (/^3[47]/.test(number)) {
      return "./assets/images/cards/amex-card.jpeg";
    } else if (/^4/.test(number)) {
      return "./assets/images/cards/visa-card.jpeg";
    } else if (/^5[1-5]/.test(number)) {
      return "./assets/images/cards/mastercard-card.jpeg";
    } else if (/^6/.test(number)) {
      return "./assets/images/cards/discover-card.jpeg";
    } else {
      return "./assets/images/cards/default-card.jpeg"; // Path to a default image if card type is unknown
    }
  };
  const cardImagePath = useMemo(
    () => getCardImagePath(cardInfo?.cardNumber),
    [cardInfo?.cardNumber]
  );
  const formatCardNumber = (number) => {
    const cleanNumber = number.replace(/\D/g, "");
    const isAmex = cleanNumber.length === 15;
    const displayDigits = isAmex ? 3 : 4;
    const maskedNumber =
      cleanNumber.slice(0, -displayDigits).replace(/\d/g, "x") +
      cleanNumber.slice(-displayDigits);
    return `${maskedNumber.slice(0, 4)}-${maskedNumber.slice(
      4,
      8
    )}-${maskedNumber.slice(8, 12)}-${maskedNumber.slice(12)}`;
  };
  return (
    <section className="container mx-auto flex-1 p-5 flex flex-col lg:max-w-full relative text-gray-700">
      <Items
        subTotal={subTotal}
        shippingCost={shippingRate}
        total={total}
      />
      {/* <div className="space-y-2 md:self-end ">
        <label htmlFor="discount" className="block font-medium">
          Discount Code:
        </label>
        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
          <input
            type="text"
            id="discount"
            value={discountCode}
            disabled={isDiscountApplied}
            onChange={(e) => {
              setDiscountCode(e.target.value);
              setError(false);
            }}
            className="border md:max-w-[11.9rem] border-gray-300 rounded-md px-4 py-2 w-full max-w-[100%] focus:ring-1 focus:ring-inset focus:ring-[#00adef] focus:outline-transparent"
            placeholder="Enter discount code"
          />
          <Button
            className="uppercase text-xs md:text-base px-6 py-2 rounded-full font-light inline-flex items-center justify-center gap-2 min-w-[110px] min-h-[42px]"
            onClick={validateDiscountCode}
            disabled={isSubmittingDiscount}
          >
            {isSubmittingDiscount ? (
              <div className="flex items-center justify-center">
                <div
                  className="inline-block w-3 h-3 md:h-5 md:w-5 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                  role="status"
                >
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            ) : (
              <>Apply</>
            )}
          </Button>
        </div>

        {isValid && (
          <p className="text-green-600">Discount applied successfully!</p>
        )}
        {error && <p className="text-red-600">{error}</p>}
      </div> */}
      <div>
        <p className="text-lg border-b py-3 w-full">Payment Information</p>
        <div className="flex justify-between items-center py-6 border-b">
          {paymentMethod === "credit" && (
            <>
              <span className="text-base md:text-lg">Card number</span>
              <div className="flex gap-2 items-center">
                <img
                  alt="card"
                  src={cardImagePath}
                  className="w-[35px] md:w-[50px]"
                />
                <span className="text-sm md:text-base">
                  {formatCardNumber(cardInfo?.cardNumber)}
                </span>
              </div>
            </>
          )}
          {paymentMethod === "paypal" && (
            <>
              <div className="flex gap-2 items-center">
                <img
                  alt="visa-card"
                  src={`./assets/images/cards/paypal.png`}
                  className="w-[100px]"
                />
              </div>
            </>
          )}
        </div>
      </div>
      <BillingInfo billingInfo={billingInfo} />
      <Turnstile
        onToken={handleTurnstileToken}
        onUnavailable={() => setIsTurnstileUnavailable(true)}
        resetKey={turnstileResetKey}
      />
      <div className="w-full flex justify-between items-center mt-5 md:mt-10 mb-7 gap-2">
        <NavLink key="b3 bands" to="/payment">
          <Button className="uppercase text-xs md:text-base px-4 py-1 rounded-full font-light w-auto">
            <div className="inline-block pr-1 relative top-[3px]">
              <ArrowLeftIcon
                fill="white"
                className="h-[0.9rem] md:h-[1.1rem] w-[1.1rem]"
                aria-hidden="true"
                color="white"
              />
            </div>
            {"Back"}
          </Button>
        </NavLink>
        {paymentMethod === "credit" && !shippingRateLoading && (
          <Button
            className="uppercase text-xs md:text-base px-6 py-2 rounded-full font-light w-auto"
            onClick={submit}
            disabled={isSubmitting || !(isTurnstileReady || isTurnstileUnavailable)}
          >
            {isSubmitting ? (
              <div class="flex items-center justify-center">
                <div
                  class="inline-block w-5 h-5 md:h-8 md:w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                  role="status"
                >
                  <span class="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                    Loading...
                  </span>
                </div>
              </div>
            ) : (
              <>
                Submit
                <div className="inline-block pl-1 relative top-[3px] ml-auto">
                  <ArrowRightIcon
                    fill="white"
                    className="h-[0.9rem] md:h-[1.1rem] w-[1.1rem]"
                    aria-hidden="true"
                    color="white"
                  />
                </div>
              </>
            )}
          </Button>
        )}
        {paymentMethod === "paypal" && !shippingRateLoading && !isTurnstileReady && (
          <span className="text-sm text-gray-500">
            {isTurnstileUnavailable
              ? "Unable to verify your browser for PayPal. Please refresh the page or pay by credit card."
              : "Verifying your browser…"}
          </span>
        )}
        {paymentMethod === "paypal" && !shippingRateLoading && isTurnstileReady && (
          <PayPalScriptProvider
            options={{ "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID }}
          >
            <PayPalButtons
              createOrder={(data, actions) => {
                return actions.order.create({
                  purchase_units: [
                    {
                      amount: {
                        value: total.toString(),
                      },
                    },
                  ],
                });
              }}
              onApprove={(data, actions) => {
                return actions.order.capture().then((details) => {
                  submit();
                });
              }}
              style={{
                layout: "horizontal",
                height: 35,
                color: "blue",
                borderRadius: 999,
              }}
              buttonStyles={{
                layout: "custom",
                shape: "rect",
                label: "paypal",
              }}
            />
          </PayPalScriptProvider>
        )}
      </div>
      <Transition appear show={isOpenOrderFailedModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="lg:max-w-3xl max-w-xl transform rounded-2xl bg-white p-10 text-left align-middle shadow-xl transition-all sm:max-h-auto max-h-[600px] overflow-auto">
                  <Dialog.Title
                    as="h3"
                    className="text-2xl font-medium leading-6 text-[#00adef]"
                  >
                    Order Failed
                  </Dialog.Title>
                  <div>
                    <div className="mt-4">
                      <p>Your Credit Card Transaction has Declined</p>
                      <p>
                        If you believe you made an error, go Back and resubmit
                        your order
                      </p>
                      <p className="mb-3">
                        Otherwise please contact your Bank to determine the
                        reason
                      </p>
                      <p>You can also contact the B3 Support Team</p>
                      <a
                        href="mailto:support@b3sciences.com"
                        className="text-[#00adef]"
                      >
                        support@b3sciences.com
                      </a>
                    </div>
                    <div className="flex justify-center mt-3 w-full">
                      <Button
                        type="button"
                        onClick={closeModal}
                        className="text-sm text-white px-4 py-2 rounded-full font-light mt-4 md:mt-0"
                        data-mdb-ripple="true"
                        data-mdb-ripple-color="light"
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </section>
  );
}
